import React from "react";
import { CalendarDays, Home, Search } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import useReveal from "../../hooks/useReveal";

const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1638284457192-27d3d0ec51aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjBtb2Rlcm58ZW58MHx8fHwxNzc3ODExOTAxfDA&ixlib=rb-4.1.0&q=85";

const ServiceCard = ({ icon: Icon, title, description, index, testid }) => (
  <article
    className="reveal group relative p-10 md:p-12 border border-white/5 bg-[#0f0f0f] card-hover flex flex-col"
    style={{ transitionDelay: `${index * 100}ms` }}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-14">
      <span className="w-12 h-12 flex items-center justify-center border border-[#e8d4a8]/30 text-[#e8d4a8] group-hover:bg-[#e8d4a8] group-hover:text-black transition-all duration-500">
        <Icon size={20} strokeWidth={1.25} />
      </span>
      <span className="font-mono-l text-[10px] text-white/30">
        0{index + 1}
      </span>
    </div>

    <h3 className="font-display text-2xl md:text-[28px] text-white leading-snug mb-5">
      {title}
    </h3>
    <p className="text-white/60 text-sm leading-relaxed font-light">
      {description}
    </p>

    <div className="mt-10 pt-6 border-t border-white/5 flex items-center justify-between">
      <span className="font-mono-l text-[10px] text-[#e8d4a8]">
        Learn more
      </span>
      <span className="text-[#e8d4a8] transition-transform duration-300 group-hover:translate-x-1">
        →
      </span>
    </div>
  </article>
);

const Services = () => {
  const { t } = useLanguage();
  const ref = useReveal();

  const services = [
    { ...t.services.short, icon: CalendarDays },
    { ...t.services.long, icon: Home },
    { ...t.services.custom, icon: Search },
  ];

  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#070707] overflow-hidden"
    >
      {/* Decorative interior image floating */}
      <div className="hidden lg:block absolute -right-20 top-20 w-[420px] h-[520px] opacity-[0.12] rotate-3">
        <img
          src={INTERIOR_IMG}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="mb-16 max-w-3xl">
          <p
            className="reveal font-mono-l text-[11px] text-[#e8d4a8] mb-5 flex items-center gap-3"
            data-testid="services-eyebrow"
          >
            <span className="w-6 h-px bg-[#e8d4a8]"></span>
            {t.services.eyebrow}
          </p>
          <h2
            className="reveal reveal-delay-1 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6"
            data-testid="services-title"
          >
            {t.services.title}
          </h2>
          <p className="reveal reveal-delay-2 text-white/60 text-base md:text-lg font-light leading-relaxed">
            {t.services.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {services.map((s, i) => (
            <ServiceCard
              key={i}
              icon={s.icon}
              title={s.title}
              description={s.description}
              index={i}
              testid={`service-card-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
